<template>
  <main class="staff-page">
    <header v-if="contentRef?.property" class="page-heading property-hero">
      <div class="min-w-0">
        <div class="page-eyebrow">Объект размещения</div>
        <h1 class="hero-title">{{ contentRef.property.name }}</h1>
      </div>
      <div class="page-actions">
        <Button v-if="contentRef.property.wiki_hyperlink" as-child variant="outline" size="sm">
          <a :href="contentRef.property.wiki_hyperlink" target="_blank" rel="noopener noreferrer">
            <BookOpen :size="16" />
            Документация
          </a>
        </Button>
        <details class="action-menu">
          <summary aria-label="Дополнительные действия" title="Дополнительные действия">
            <Ellipsis :size="19" />
          </summary>
          <div class="action-menu-content">
            <Button variant="ghost" class="justify-start" @click="copyEmbedCode">
              <CodeXml :size="16" />
              Код для встраивания
            </Button>
            <Button
              variant="ghost"
              class="justify-start"
              @click="copyToClipboard(embedUrl, 'Ссылка скопирована!')"
            >
              <Link :size="16" />
              Ссылка на embed
            </Button>
          </div>
        </details>
      </div>
    </header>
    <property-customer-tasks-link
      v-if="!contentRef?.loading && contentRef?.property?.customer_tasks?.url"
      :tasks="contentRef.property.customer_tasks"
    />
    <Dialog v-model:open="embedDialog">
      <DialogContent class="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Код для встраивания</DialogTitle>
          <DialogDescription>Ссылка и код iframe для документации</DialogDescription>
        </DialogHeader>
        <div class="space-y-5">
          <div>
            <div class="flex items-center justify-between gap-2 mb-2">
              <label for="embed-url" class="ui-label">Прямая ссылка</label>
              <Button
                size="sm"
                variant="outline"
                @click="copyToClipboard(embedUrl, 'Ссылка скопирована!')"
              >
                <Copy :size="14" />
                Копировать
              </Button>
            </div>
            <Input id="embed-url" :model-value="embedUrl" readonly />
          </div>
          <div>
            <div class="flex items-center justify-between gap-2 mb-2">
              <label for="embed-code" class="ui-label">Код iframe</label>
              <Button
                size="sm"
                variant="outline"
                @click="copyToClipboard(embedCode, 'Код iframe скопирован!')"
              >
                <Copy :size="14" />
                Копировать
              </Button>
            </div>
            <Textarea
              id="embed-code"
              :model-value="embedCode"
              readonly
              rows="4"
              class="code-textarea font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="embedDialog = false">Закрыть</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <div v-if="snackbar" class="staff-toast" role="status">
      {{ snackbarMessage }}
      <Button variant="ghost" size="sm" @click="snackbar = false">Закрыть</Button>
    </div>
    <property-detail-content
      ref="contentRef"
      :property-id="id"
      :highlighted-contact-id="highlightedContactId"
    />
  </main>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { BookOpen, Ellipsis, CodeXml, Link, Copy } from '@lucide/vue';

import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import PropertyDetailContent from '@/components/PropertyDetailContent.vue';
import PropertyCustomerTasksLink from '@/components/PropertyCustomerTasksLink.vue';

interface Props {
  id: number;
}

const props = defineProps<Props>();
const route = useRoute();

const highlightedContactId = ref<number | null>(null);
const contentRef = ref<InstanceType<typeof PropertyDetailContent> | null>(null);
const snackbar = ref(false);
const snackbarMessage = ref('');
const embedDialog = ref(false);
let notificationTimer: ReturnType<typeof setTimeout> | null = null;
watch(snackbar, (show) => {
  if (notificationTimer) clearTimeout(notificationTimer);
  if (show)
    notificationTimer = setTimeout(() => {
      snackbar.value = false;
    }, 3000);
});
onBeforeUnmount(() => {
  if (notificationTimer) clearTimeout(notificationTimer);
});

const embedUrl = computed(() => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/embed/properties/${props.id}/`;
});

const embedCode = computed(() => {
  return `<iframe src="${embedUrl.value}" width="100%" height="800" frameborder="0" style="border: none; min-height: 600px;"></iframe>`;
});

function copyEmbedCode() {
  embedDialog.value = true;
}

async function copyToClipboard(text: string, message: string) {
  try {
    await navigator.clipboard.writeText(text);
    snackbarMessage.value = message;
    snackbar.value = true;
  } catch (err) {
    console.error('Ошибка копирования в буфер обмена:', err);
    snackbarMessage.value = 'Ошибка копирования. Попробуйте еще раз.';
    snackbar.value = true;
  }
}

onMounted(() => {
  const contactIdParam = route.query.contactId;
  if (contactIdParam) {
    highlightedContactId.value = parseInt(contactIdParam as string, 10);
  }
});
</script>
